// "There is already a place here" -- the ask that stands between a gesture and
// a new TaskPogoPlace row when one already exists within NEAR_PLACE_NM.
//
// It exists because every geocoder-driven place write in this app is create-
// only BY NAME: a spelling that differs by a comma or a word order is a
// different nameKey, the guard passes, and a second row lands a few hundred
// metres from the first. Nothing else compares two rows by position, and the
// only thing that can undo one afterwards is scripts/merge-places.mjs.
//
// It WARNS, it never blocks -- the same bargain findDuplicatePlaces strikes for
// exact-name collisions, and for the same reason: two marinas really can share
// a dock, so the user is the one who knows. So the matches are NAMED rather
// than counted (CopyLegToLogModal's rule), and each carries its distance,
// because "0.1 nm" and "0.4 nm" is the whole difference between "the same
// marina" and "the one next door".
//
// DISMISSING CREATES NOTHING. Escape, the close button and the backdrop all
// mean "I didn't decide" -- and of the two outcomes, not writing a row is the
// recoverable one. The caller keeps whatever position the gesture already
// applied; only the shared-database write is being decided here.
//
// Full names throughout, not placeDisplayName: this is a dialog with room, and
// telling two similarly-named rows apart is the entire task. Same reason
// PlacesTable shows the full name.
//
// The wording props default to the trip-side flow (binding a waypoint to an
// existing row); /me/places overrides them, since there the alternative to
// creating is opening the row for editing rather than binding anything.
//
// Props:
//   show         - visibility
//   onHide       - dismissed; create nothing, decide nothing
//   places       - the nearby rows, nearest first, each with `distanceNm`
//   onUse(place) - user picked an existing row
//   onCreate()   - user wants a new place anyway
//   title        - modal heading
//   intro(count) - line above the list
//   useLabel     - prefix for each row's button
//   createLabel  - label of the create-anyway button
//   progress     - optional "2 of 3" when several questions are queued
//
// No `busy`, deliberately, unlike CopyLegToLogModal. Nothing here is awaited:
// both writers (recordPickedPlace, bindTargetToPlace) are fire-and-forget or
// synchronous, and /me/places closes the dialog before its create starts. There
// is no in-flight state to lock against, so a prop for one would be an unused
// API with a test asserting behaviour no caller can reach. Answering twice is
// prevented at the source instead -- see useRouteLocator's `answeredRef`.

import Button from 'react-bootstrap/Button';
import Modal from 'react-bootstrap/Modal';
import { formatNearbyNm } from '../fleet-shared/PlaceFields.mjs';

// At most this many rows get a button. Past a handful the list stops helping
// pick and starts being something to read, and the answer is almost always the
// nearest one or two.
export const MAX_NEARBY_SHOWN = 5;

const defaultIntro = (count) =>
  count === 1
    ? 'There is already a place here:'
    : `There are already ${count} places here:`;

export const NearbyPlacesModal = ({
  show,
  onHide,
  places = [],
  onUse,
  onCreate,
  title = 'Use the existing place?',
  intro = defaultIntro,
  useLabel = 'Use',
  createLabel = 'Add as a new place',
  progress,
}) => {
  const shown = places.slice(0, MAX_NEARBY_SHOWN);

  return (
    <Modal show={show} onHide={onHide}>
      <Modal.Header closeButton>
        <Modal.Title>{title}</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        {progress && <p className="text-body-secondary small mb-1">{progress}</p>}
        <p className="text-body-secondary">{intro(places.length)}</p>
        <div className="d-grid gap-2">
          {shown.map((place) => (
            <Button
              key={place.id}
              variant="outline-primary"
              className="text-start"
              onClick={() => onUse(place)}
            >
              {useLabel} {place.name}
              {/* The leading space is inside the span on purpose: `ms-1` spaces
                  it on screen, but the ACCESSIBLE name is the concatenated
                  text, and without it a screen reader (and a Playwright
                  locator) reads "Cap Sante— 0.2 nm away". */}
              <span className="text-body-secondary ms-1">
                {' '}— {formatNearbyNm(place.distanceNm)} away
                {place.curated ? ', curated' : ''}
              </span>
            </Button>
          ))}
        </div>
        {places.length > shown.length && (
          <p className="text-body-secondary small mt-2 mb-0">
            {places.length - shown.length} more nearby.
          </p>
        )}
      </Modal.Body>
      <Modal.Footer className="d-flex flex-wrap justify-content-between">
        <Button variant="outline-secondary" onClick={onCreate}>
          <i className="fa-solid fa-plus me-1" aria-hidden="true"></i>
          {createLabel}
        </Button>
        <Button variant="secondary" onClick={onHide}>
          Cancel
        </Button>
      </Modal.Footer>
    </Modal>
  );
};

export default NearbyPlacesModal;
